import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { mcsApi } from '../../../api/mcs/mcsApi';
import { IOrderResponseModel, OrderStatus } from '../../../api/mcs/interfaces';
import { notifyError, notifySuccess } from '../../../api/axios';
import { RootState } from '../../store';
import { t } from 'i18next';

interface IOrdersState {
    unassignedOrders: IOrderResponseModel[];
    selectedOrderIds: number[];
    currentOrder: IOrderResponseModel | null;
    loading: boolean;
    fetchingOrder: boolean;
    error: string | null;
}

const initialState: IOrdersState = {
    unassignedOrders: [],
    selectedOrderIds: [],
    currentOrder: null,
    loading: false,
    fetchingOrder: false,
    error: null,
};

export const fetchUnassignedOrders = createAsyncThunk(
    'mcs/orders/fetchUnassigned',
    async (
        {
            pageSize = 50,
            pageNumber = 1,
        }: { pageSize?: number; pageNumber?: number },
        { rejectWithValue },
    ) => {
        try {
            const orders = await mcsApi.orders.getUnassignedOrders(
                pageSize,
                pageNumber,
            );
            return orders;
        } catch (error: unknown) {
            notifyError(t('MCS:failedToFetchOrders'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:failedToFetchOrders');
            return rejectWithValue(message);
        }
    },
);

export const fetchOrdersByStatus = createAsyncThunk(
    'mcs/orders/fetchByStatus',
    async (
        {
            status,
            pageSize = 50,
            pageNumber = 1,
        }: { status: string; pageSize?: number; pageNumber?: number },
        { rejectWithValue },
    ) => {
        try {
            const orders = await mcsApi.orders.getOrdersByStatus(
                status,
                pageSize,
                pageNumber,
            );
            return orders;
        } catch (error: unknown) {
            notifyError(t('MCS:failedToFetchOrders'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('failedToFetchOrders');
            return rejectWithValue(message);
        }
    },
);

export const fetchOrdersByCustomer = createAsyncThunk(
    'mcs/orders/fetchByCustomer',
    async (
        {
            customerId,
            status,
            pageSize = 50,
            pageNumber = 1,
        }: {
            customerId: number;
            status?: OrderStatus;
            pageSize?: number;
            pageNumber?: number;
        },
        { rejectWithValue },
    ) => {
        try {
            const orders = await mcsApi.orders.getOrdersByCustomer(
                customerId,
                status,
                pageSize,
                pageNumber,
            );
            return orders;
        } catch (error: unknown) {
            notifyError(t('MCS:failedToFetchOrders'));
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:failedToFetchOrders');
            return rejectWithValue(message);
        }
    },
);

export const fetchOrderById = createAsyncThunk(
    'mcs/orders/fetchById',
    async (
        {
            orderId,
            includeAll = true,
        }: { orderId: number; includeAll?: boolean },
        { rejectWithValue },
    ) => {
        try {
            const order = await mcsApi.orders.getOrderById(orderId, includeAll);
            return order;
        } catch (error: unknown) {
            notifyError(`${t('MCS:failedToFetchOrder')} with ID ${orderId}`);
            const message =
                error instanceof Error
                    ? error.message
                    : `${t('MCS:failedToFetchOrder')} with ID ${orderId}`;
            return rejectWithValue(message);
        }
    },
);

export const deleteOrderAsync = createAsyncThunk(
    'mcs/orders/delete',
    async (orderId: number, { rejectWithValue }) => {
        try {
            await mcsApi.orders.deleteOrder(orderId);
            notifySuccess(t('MCS:orderDeletionSuccess'));
            return orderId;
        } catch (error: unknown) {
            notifyError(`${t('MCS:orderDeletionFailure')} ${orderId}`);
            const message =
                error instanceof Error
                    ? error.message
                    : t('MCS:orderDeletionFailure') + ` ${orderId}`;
            return rejectWithValue(message);
        }
    },
);

const ordersSlice = createSlice({
    name: 'mcsOrders',
    initialState,
    reducers: {
        toggleOrderSelection: (state, action: PayloadAction<number>) => {
            const orderId = action.payload;
            const index = state.selectedOrderIds.indexOf(orderId);

            if (index > -1) {
                state.selectedOrderIds.splice(index, 1);
            } else {
                state.selectedOrderIds.push(orderId);
            }
        },

        selectAllOrders: state => {
            state.selectedOrderIds = state.unassignedOrders
                .filter(order => order.isActive)
                .map(order => order.orderId);
        },

        deselectAllOrders: state => {
            state.selectedOrderIds = [];
        },

        setSelectedOrders: (state, action: PayloadAction<number[]>) => {
            state.selectedOrderIds = action.payload;
        },

        clearCurrentOrder: state => {
            state.currentOrder = null;
        },

        clearError: state => {
            state.error = null;
        },
    },
    extraReducers: builder => {
        builder.addCase(fetchUnassignedOrders.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchUnassignedOrders.fulfilled, (state, action) => {
            state.loading = false;
            state.unassignedOrders = action.payload;
            state.selectedOrderIds = [];
        });
        builder.addCase(fetchUnassignedOrders.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        builder.addCase(fetchOrdersByStatus.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchOrdersByStatus.fulfilled, (state, action) => {
            state.loading = false;
            state.unassignedOrders = action.payload;
        });
        builder.addCase(fetchOrdersByStatus.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        builder.addCase(fetchOrdersByCustomer.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(fetchOrdersByCustomer.fulfilled, (state, action) => {
            state.loading = false;
            state.unassignedOrders = action.payload;
        });
        builder.addCase(fetchOrdersByCustomer.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });

        builder.addCase(fetchOrderById.pending, state => {
            state.fetchingOrder = true;
            state.error = null;
        });
        builder.addCase(fetchOrderById.fulfilled, (state, action) => {
            state.fetchingOrder = false;
            state.currentOrder = action.payload;
        });
        builder.addCase(fetchOrderById.rejected, (state, action) => {
            state.fetchingOrder = false;
            state.error = action.payload as string;
        });

        builder.addCase(deleteOrderAsync.pending, state => {
            state.loading = true;
            state.error = null;
        });
        builder.addCase(deleteOrderAsync.fulfilled, (state, action) => {
            state.loading = false;
            state.unassignedOrders = state.unassignedOrders.filter(
                order => order.orderId !== action.payload,
            );
            state.selectedOrderIds = state.selectedOrderIds.filter(
                id => id !== action.payload,
            );
        });
        builder.addCase(deleteOrderAsync.rejected, (state, action) => {
            state.loading = false;
            state.error = action.payload as string;
        });
    },
});

export const {
    toggleOrderSelection,
    selectAllOrders,
    deselectAllOrders,
    setSelectedOrders,
    clearCurrentOrder,
    clearError,
} = ordersSlice.actions;

export const selectUnassignedOrders = (
    state: RootState,
): IOrderResponseModel[] => state.mcsOrders.unassignedOrders;
export const selectSelectedOrderIds = (state: RootState): number[] =>
    state.mcsOrders.selectedOrderIds;
export const selectCurrentOrder = (
    state: RootState,
): IOrderResponseModel | null => state.mcsOrders.currentOrder;
export const selectOrdersLoading = (state: RootState): boolean =>
    state.mcsOrders.loading;
export const selectFetchingOrder = (state: RootState): boolean =>
    state.mcsOrders.fetchingOrder;
export const selectOrdersError = (state: RootState): string | null =>
    state.mcsOrders.error;
export const selectHasSelectedOrders = (state: RootState): boolean =>
    state.mcsOrders.selectedOrderIds.length > 0;
export const selectSelectedOrdersCount = (state: RootState): number =>
    state.mcsOrders.selectedOrderIds.length;

export default ordersSlice.reducer;
